import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { EditClientForm } from "@/lib/core/crm/types/contacts";

interface ClientContactSectionProps {
  editForm: UseFormReturn<EditClientForm>;
  isOpen?: boolean;
  onToggle?: () => void;
  /** When true, render only the fields (no wrapper, no header). Used by FormSectionGroup. */
  headerless?: boolean;
}

const Fields: React.FC<{ editForm: UseFormReturn<EditClientForm> }> = ({ editForm }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="edit-name">
        Name <span className="text-red-500">*</span>
      </Label>
      <Input
        id="edit-name"
        {...editForm.register("name", {
          required: "Name is required",
          minLength: { value: 2, message: "Name must be at least 2 characters" },
        })}
        placeholder="Client name"
        aria-describedby={editForm.formState.errors.name ? "name-error" : undefined}
      />
      {editForm.formState.errors.name && (
        <p id="name-error" className="text-sm text-red-600">
          {editForm.formState.errors.name.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-company">Company Name</Label>
      <Input
        id="edit-company"
        {...editForm.register("company_name", {
          minLength: { value: 2, message: "Company name must be at least 2 characters" },
        })}
        placeholder="Company name"
        aria-describedby={editForm.formState.errors.company_name ? "company-error" : undefined}
      />
      {editForm.formState.errors.company_name && (
        <p id="company-error" className="text-sm text-red-600">
          {editForm.formState.errors.company_name.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-email">
        Email <span className="text-red-500">*</span>
      </Label>
      <Input
        id="edit-email"
        type="email"
        {...editForm.register("email", {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address",
          },
        })}
        placeholder="client@example.com"
        aria-describedby={editForm.formState.errors.email ? "email-error" : undefined}
      />
      {editForm.formState.errors.email && (
        <p id="email-error" className="text-sm text-red-600">
          {editForm.formState.errors.email.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-phone">Phone</Label>
      <Input
        id="edit-phone"
        type="tel"
        {...editForm.register("phone")}
        placeholder="(555) 123-4567"
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-website">Website</Label>
      <Input
        id="edit-website"
        type="url"
        {...editForm.register("website", {
          pattern: {
            value: /^https?:\/\/.+/,
            message: "Please enter a valid website URL (starting with http:// or https://)",
          },
        })}
        placeholder="https://acme.com"
        aria-describedby={editForm.formState.errors.website ? "website-error" : undefined}
      />
      {editForm.formState.errors.website && (
        <p id="website-error" className="text-sm text-red-600">
          {editForm.formState.errors.website.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-industry">Industry</Label>
      <Input id="edit-industry" {...editForm.register("industry")} placeholder="Industry" />
    </div>
  </>
);

export const ClientContactSection: React.FC<ClientContactSectionProps> = ({
  editForm,
  isOpen,
  onToggle,
  headerless,
}) => {
  if (headerless) {
    return (
      <div className="space-y-4">
        <Fields editForm={editForm} />
      </div>
    );
  }
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="w-full flex justify-between items-center px-4 py-3 bg-card-50 hover:bg-card-100 font-semibold text-left"
        onClick={onToggle}
      >
        Contact Information
        <span>{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && (
        <div className="p-4 space-y-4">
          <Fields editForm={editForm} />
        </div>
      )}
    </div>
  );
};
